import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Calendar, MapPin, Users, Mail, Download } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { title: "", kind: "szkolenie", description: "", long_description: "", agenda: "", price: "", price_includes: "", packages: [], location: "", event_date: "", end_date: "", capacity: 0, images: [] };

export default function AdminTrainings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [signupsFor, setSignupsFor] = useState(null);
  const [signups, setSignups] = useState([]);
  const [regs, setRegs] = useState([]);

  const load = () => api.get("/trainings").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...t, event_date: (t.event_date || "").slice(0, 10) }); setOpen(true); };

  const openSignups = async (t) => {
    setSignupsFor(t);
    try { const { data } = await api.get(`/trainings/${t.id}/signups`); setSignups(data); }
    catch { setSignups([]); }
    try { const { data } = await api.get(`/trainings/${t.id}/registrations`); setRegs(data); }
    catch { setRegs([]); }
  };

  const delReg = async (rid) => {
    try { await api.delete(`/registrations/${rid}`); setRegs((x) => x.filter((r) => r.id !== rid)); toast.success("Usunięto zgłoszenie"); }
    catch { toast.error("Błąd usuwania"); }
  };

  const exportCsv = async (t) => {
    try {
      const res = await api.get(`/trainings/${t.id}/signups/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `zgloszenia_${(t.title || "szkolenie").replace(/[^a-z0-9]+/gi, "_").slice(0, 40)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Błąd eksportu"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, event_date: form.event_date ? new Date(form.event_date).toISOString() : "" };
      if (editing) await api.put(`/trainings/${editing.id}`, payload);
      else await api.post("/trainings", payload);
      toast.success("Zapisano");
      setOpen(false);
      load();
    } catch { toast.error("Błąd zapisu"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Usunąć to wydarzenie?")) return;
    await api.delete(`/trainings/${id}`);
    toast.success("Usunięto");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Szkolenia i wydarzenia</h2>
          <p className="mt-1 text-sm text-muted-foreground">Dodawaj wydarzenia z datą, miejscem, zdjęciami i zbieraj zgłoszenia</p>
        </div>
        <Button onClick={openNew} className="gap-1.5 bg-ztk-blue" data-testid="training-add"><Plus className="h-4 w-4" /> Nowe wydarzenie</Button>
      </div>
      <div className="mt-8 space-y-3">
        {items.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4" data-testid={`admin-training-${t.id}`}>
            <div className="min-w-0">
              <h3 className="font-display font-semibold text-ztk-navy dark:text-white">{t.title}</h3>
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{t.event_date ? new Date(t.event_date).toLocaleDateString("pl-PL") : "—"}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{t.location}</span>
                <span>{t.images?.length || 0} zdjęć</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{t.signup_count || 0}{t.capacity ? ` / ${t.capacity}` : " zapisanych"}</span>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="outline" onClick={() => openSignups(t)} className="gap-1.5" data-testid={`training-signups-${t.id}`}><Users className="h-3.5 w-3.5" /> Zgłoszenia</Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`training-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(t.id)} className="text-destructive" data-testid={`training-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edytuj wydarzenie" : "Nowe wydarzenie"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Typ wydarzenia</Label>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="training-kind" className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="szkolenie">Szkolenie</option>
                <option value="konferencja">Konferencja</option>
              </select>
            </div>
            <div><Label>Tytuł</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="training-title" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{form.kind === "konferencja" ? "Data rozpoczęcia" : "Data"}</Label><Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="training-date" className="mt-1.5" /></div>
              <div><Label>Miejsce</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="training-location" className="mt-1.5" /></div>
            </div>
            {form.kind === "konferencja" && <div><Label>Data zakończenia</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="training-end-date" className="mt-1.5 w-full sm:w-60" /></div>}
            <div><Label>Limit miejsc (0 = bez limitu)</Label><Input type="number" min={0} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} data-testid="training-capacity" className="mt-1.5 w-40" /></div>
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <Label>Pakiety cenowe (opcjonalnie)</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, packages: [...(form.packages || []), { name: "", price: "", includes: "" }] })} data-testid="package-add">+ Dodaj pakiet</Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Np. Standard / VIP. Każdy pakiet: nazwa, cena i lista zawartości (każda linia = punkt). Pokażą się na podstronie szkolenia.</p>
              <div className="mt-3 space-y-3">
                {(form.packages || []).map((p, i) => (
                  <div key={i} className="rounded-lg bg-secondary/50 p-3" data-testid={`package-row-${i}`}>
                    <div className="flex gap-2">
                      <Input value={p.name} onChange={(e) => { const ps = [...form.packages]; ps[i] = { ...ps[i], name: e.target.value }; setForm({ ...form, packages: ps }); }} placeholder="Nazwa (np. Standard)" data-testid={`package-name-${i}`} />
                      <Input value={p.price} onChange={(e) => { const ps = [...form.packages]; ps[i] = { ...ps[i], price: e.target.value }; setForm({ ...form, packages: ps }); }} placeholder="Cena (np. 500 zł)" className="w-40" data-testid={`package-price-${i}`} />
                      <Button type="button" size="icon" variant="ghost" className="shrink-0 text-destructive" onClick={() => setForm({ ...form, packages: form.packages.filter((_, j) => j !== i) })} data-testid={`package-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <Textarea value={p.includes} onChange={(e) => { const ps = [...form.packages]; ps[i] = { ...ps[i], includes: e.target.value }; setForm({ ...form, packages: ps }); }} className="mt-2" rows={3} placeholder={"Co zawiera ten pakiet (każda linia = punkt)"} data-testid={`package-includes-${i}`} />
                  </div>
                ))}
                {(form.packages || []).length === 0 && <p className="text-xs text-muted-foreground">Brak pakietów. Kliknij „+ Dodaj pakiet", aby dodać cennik.</p>}
              </div>
            </div>
            <div><Label>Krótki opis (widoczny na liście szkoleń)</Label><div className="mt-1.5"><RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="training-desc" placeholder="Jedno–dwa zdania na kafelek listy..." /></div></div>
            <div><Label>Szczegółowy opis (podstrona szkolenia)</Label><p className="mt-1 text-xs text-muted-foreground">Pełna treść widoczna dopiero po wejściu w szkolenie — program, agenda, prelegenci, dojazd.</p><div className="mt-1.5"><RichTextEditor value={form.long_description} onChange={(v) => setForm({ ...form, long_description: v })} testid="training-long-desc" placeholder="Pełny opis wydarzenia..." /></div></div>
            {form.kind === "konferencja" && <div><Label>Agenda konferencji</Label><div className="mt-1.5"><RichTextEditor value={form.agenda} onChange={(v) => setForm({ ...form, agenda: v })} testid="training-agenda" placeholder="Program: godziny, sesje, prelegenci..." /></div></div>}
            <ImageUpload value={form.images} multiple label="Galeria zdjęć" onChange={(v) => setForm({ ...form, images: v })} />
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-ztk-blue" data-testid="admin-save-event-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Zapisz</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signupsFor} onOpenChange={(o) => !o && setSignupsFor(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Zgłoszenia — {signupsFor?.title}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Łącznie zapisanych: <strong>{signups.length}</strong>{signupsFor?.capacity ? ` / ${signupsFor.capacity}` : ""}</p>
            {signups.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => exportCsv(signupsFor)} className="gap-1.5" data-testid="signups-export">
                <Download className="h-3.5 w-3.5" /> Eksport CSV / Excel
              </Button>
            )}
          </div>
          {regs.length > 0 && (
            <div className="mt-4 rounded-lg border border-border p-3" data-testid="registrations-list">
              <p className="mb-2 text-sm font-semibold text-ztk-navy dark:text-white">Zgłoszenia z formularza „Weź udział" ({regs.length})</p>
              <div className="space-y-2">
                {regs.map((r) => (
                  <div key={r.id} className="rounded-md bg-secondary/50 p-2 text-xs" data-testid={`reg-row-${r.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-ztk-navy dark:text-white">{r.company || "—"} · {r.count} os.</span>
                      <div className="flex items-center gap-2">
                        <a href={`mailto:${r.email}`} className="text-ztk-blue">{r.email}</a>
                        <button type="button" onClick={() => delReg(r.id)} className="text-destructive" data-testid={`reg-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    <p className="mt-0.5 text-muted-foreground">Tel: {r.phone || "—"}{r.participants ? ` · ${r.participants}` : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-2 space-y-2" data-testid="signups-list">
            {signups.length === 0 && <p className="text-sm text-muted-foreground">Brak zgłoszeń.</p>}
            {signups.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
                <span className="font-medium text-ztk-navy dark:text-white">{s.user_name}</span>
                <a href={`mailto:${s.user_email}`} className="flex items-center gap-1 text-ztk-blue"><Mail className="h-3.5 w-3.5" />{s.user_email}</a>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
